/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useQuery, UseQueryResult} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {getSFTPConfigurations} from '@ion/core/src/services/Reports';
import {SFTPConnection} from '@ion/core/src/types/entities';
import {sortByString} from '@ion/core/src/utils/ArrayUtil';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';

/**
 * The UseSFTPConfigurationsProps interface defines the set of properties that
 * can be passed as parameters to the useSFTPConfigurations hook.
 */
export interface UseSFTPConfigurationsProps {
  accountId: number;
  enabled?: boolean;
};

/**
 * The UseSFTPConfigurationsResults type represents the result
 * returned by the useSFTPConfigurations hook, providing information
 * about the SFTP configurations and a function to refresh the query.
 */
export type UseSFTPConfigurationsResults = {
  configurations: SFTPConnection[];
  refresh: Function;
} & UseQueryResult;

/**
 * The useSFTPConfigurations hook is a React Query hook designed to
 * fetch SFTP configurations for a specific account. It provides a
 * convenient way to manage and interact with SFTP configurations.
 *
 * ```typescript
 *
 *
import {useSFTPConfigurations} from '@ion/query/src/hooks/useSFTPConfigurations';
 *
 *  const config = useSFTPConfigurations({accountId});
 *
 * ```
 */
export const useSFTPConfigurations = ({
  accountId,
  enabled = true
}: UseSFTPConfigurationsProps) => {
  const query = useQuery(
    createKey(QueryKey.GET_SFTP_CONFIGURATIONS, accountId),
    async() => {
      try {
        const response: any = await getSFTPConfigurations(accountId);
        return sortByString(response.data.results || [], 'serverName');
      } catch (err) {
        error(err.message);
        return [];
      }
    },
    {enabled}
  );

  const refresh = useCallback(() => {
    query.refetch();
  }, [query]);

  return createQueryResult(query, {
    configurations : query.data,
    refresh
  }) as UseSFTPConfigurationsResults;
};

export default useSFTPConfigurations;
